import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { showAlert } from '../../../general-components/alert-service';
import { ALERT_TYPES } from '../../../general-components/alert-types';
import colors from '../../../../utils/colors';
import { GreenroomState } from '../../../../stores/types/types';
import { getSyncHeaders } from '../../../../connection/utils';

/**
 * Debug tool for testing content generation error handling
 * This component helps test the checkJob function error scenarios from capsule.ts
 */

const SYNC_HOST = process.env.REACT_APP_SYNC_HOST;

const ERROR_SCENARIOS = {
	FAILED: {
		title: 'Failed Status',
		description: 'Simulates checkJob returning status: "failed"',
		value: 'failed',
		mockData: {
			status: '200',
			message: 'OK',
			data: {
				jobId: 'test-job-123',
				status: 'failed',
				error: 'Mocked failure for testing retry logic',
			},
		},
	},
	SERVER_ERROR: {
		title: 'Server Error (500)',
		description: 'Simulates checkJob returning status: "server_error"',
		value: 'server_error',
		mockData: {
			status: '500',
			message: 'Internal Server Error',
			data: {
				jobId: 'test-job-123',
				status: 'server_error',
				error: 'Capsule API server error - please try again later',
			},
		},
	},
	NOT_FOUND: {
		title: 'Not Found (404)',
		description: 'Simulates checkJob returning status: "not_found"',
		value: 'not_found',
		mockData: {
			status: '404',
			message: 'Job not found',
			data: {
				jobId: 'test-job-123',
				status: 'not_found',
				error: 'Job not found or may have expired',
			},
		},
	},
	BAD_REQUEST: {
		title: 'Bad Request (400)',
		description: 'Simulates checkJob returning status: "bad_request"',
		value: 'bad_request',
		mockData: {
			status: '400',
			message: 'Bad request',
			data: {
				jobId: 'test-job-123',
				status: 'bad_request',
				error: 'Invalid job request parameters',
			},
		},
	},
	CLIENT_ERROR: {
		title: 'Client Error (4xx)',
		description: 'Simulates checkJob returning status: "client_error"',
		value: 'client_error',
		mockData: {
			status: '429',
			message: 'Client error',
			data: {
				jobId: 'test-job-123',
				status: 'client_error',
				error: 'Request failed with status 429',
			},
		},
	},
	API_ERROR: {
		title: 'Generic API Error',
		description: 'Simulates checkJob returning status: "api_error"',
		value: 'api_error',
		mockData: {
			status: 'unknown',
			message: 'API error',
			data: {
				jobId: 'test-job-123',
				status: 'api_error',
				error: 'Unknown error occurred',
			},
		},
	},
	NULL_DATA: {
		title: 'Null Data Response',
		description: 'Simulates checkJob returning 200 with null data',
		value: 'null_data',
		mockData: {
			status: '200',
			message: 'OK',
			data: null,
		},
	},
	NETWORK_TIMEOUT: {
		title: 'Network Timeout',
		description: 'Simulates a network timeout scenario',
		value: 'network_timeout',
		mockData: {
			status: 'unknown',
			message: 'API error',
			data: {
				jobId: 'test-job-123',
				status: 'api_error',
				error: 'Network timeout - request took too long',
			},
		},
	},
};

export default function DebugContentErrors() {
	const roomId = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.meeting);
	const token = useSelector((s: GreenroomState) => s.SessionReducer.token);
	const [duration, setDuration] = useState<number>(8000);
	const [customMessage, setCustomMessage] = useState<string>('Content generation failed');
	const [customDescription, setCustomDescription] = useState<string>(
		'There was an error generating your content. Please try again.'
	);
	const [activeScenario, setActiveScenario] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Load active scenario on mount
	useEffect(() => {
		if (!roomId || !token) return;
		loadActiveScenario();
	}, [roomId, token]);

	const loadActiveScenario = async () => {
		if (!roomId || !token) return;
		try {
			const response = await fetch(`${SYNC_HOST}/v1/room/${roomId}/content/debug`, {
				headers: getSyncHeaders(token),
			});
			const data = await response.json();
			setActiveScenario(data.scenario);
		} catch (error) {
			console.error('Error loading debug scenario:', error);
		}
	};

	const setDebugScenario = async (scenario: string) => {
		if (!roomId || !token) return;
		setIsLoading(true);
		try {
			const response = await fetch(`${SYNC_HOST}/v1/room/${roomId}/content/debug`, {
				method: 'POST',
				headers: getSyncHeaders(token),
				body: JSON.stringify({ scenario }),
			});
			const data = await response.json();

			if (data.success) {
				setActiveScenario(scenario);
				showAlert({
					type: ALERT_TYPES.POSITIVE,
					message: 'Debug scenario activated',
					description: `Now simulating: ${scenario}`,
					duration: 3000,
				});
			}
		} catch (error) {
			console.error('Error setting debug scenario:', error);
			showAlert({
				type: ALERT_TYPES.ERROR,
				message: 'Failed to set debug scenario',
				description: 'Check console for details',
				duration: 5000,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const clearDebugScenario = async () => {
		if (!roomId || !token) return;
		setIsLoading(true);
		try {
			const response = await fetch(`${SYNC_HOST}/v1/room/${roomId}/content/debug`, {
				method: 'DELETE',
				headers: getSyncHeaders(token),
			});
			const data = await response.json();

			if (data.success) {
				setActiveScenario(null);
				showAlert({
					type: ALERT_TYPES.NEUTRAL,
					message: 'Debug mode disabled',
					description: 'Content generation will use real API',
					duration: 3000,
				});
			}
		} catch (error) {
			console.error('Error clearing debug scenario:', error);
			showAlert({
				type: ALERT_TYPES.ERROR,
				message: 'Failed to clear debug scenario',
				description: 'Check console for details',
				duration: 5000,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const triggerGeneralError = (scenario?: keyof typeof ERROR_SCENARIOS) => {
		let message = customMessage;
		let description = customDescription;

		if (scenario) {
			const errorData = ERROR_SCENARIOS[scenario];
			message = errorData.title;
			description = `${errorData.description}\n\nMock Response:\n\`\`\`json\n${JSON.stringify(
				errorData.mockData,
				null,
				2
			)}\n\`\`\``;
		}

		showAlert({
			type: ALERT_TYPES.GENERAL_ERROR,
			message,
			description,
			duration,
		});

		// Log to console for debugging
		console.log('[Debug Content Errors] Triggered alert:', {
			type: 'GENERAL_ERROR',
			scenario,
			message,
			duration,
		});
	};


	return (
		<div style={{ padding: '16px 0' }}>
			<div className="bl-font-header text-dark margin-bottom-small">
				Content Generation Error Testing
			</div>
			<p className="bl-font-s1 text-gray-200 margin-bottom-medium">
				Test error handling for the content-service checkJob function.
			</p>

			{/* Active Scenario Status */}
			<div
				style={{
					backgroundColor: activeScenario ? colors.blGrayscale800 : colors.blGrayscale900,
					borderRadius: '8px',
					padding: '12px',
					marginBottom: '16px',
					border: `2px solid ${activeScenario ? '#4ade80' : colors.blGrayscale700}`,
				}}
			>
				<div className="bl-font-s1 text-gray-100" style={{ marginBottom: '8px' }}>
					{activeScenario ? '🟢 Debug Mode Active' : '⚪ Debug Mode Disabled'}
				</div>
				{activeScenario ? (
					<>
						<div className="bl-font-s1 text-gray-300" style={{ fontSize: '12px' }}>
							Next content generation will simulate: <strong>{activeScenario}</strong>
						</div>
						<button
							onClick={clearDebugScenario}
							disabled={isLoading}
							className="button-pillbox negative small"
							style={{ marginTop: '8px' }}
						>
							{isLoading ? 'Clearing...' : 'Disable Debug Mode'}
						</button>
					</>
				) : (
					<div className="bl-font-s1 text-gray-300" style={{ fontSize: '12px' }}>
						Content generation using real Capsule API
					</div>
				)}
			</div>

			{/* Custom Alert Controls */}
			<div className="margin-top-medium margin-bottom-medium">
				<div className="bl-font-s2 text-gray-100 margin-bottom-xsmall">Custom Alert</div>
				<div className="margin-top-small">
					<label className="bl-font-s1 text-gray-200">Title</label>
					<input
						className="margin-top-xsmall"
						onChange={(e) => setCustomMessage(e.target.value)}
						value={customMessage}
						style={{ minWidth: '100%', marginBottom: '12px' }}
					/>
				</div>
				<div className="margin-top-small">
					<label className="bl-font-s1 text-gray-200">Description</label>
					<textarea
						onChange={(e) => setCustomDescription(e.target.value)}
						value={customDescription}
						style={{
							minWidth: '100%',
							minHeight: '80px',
							marginTop: '4px',
							marginBottom: '12px',
						}}
					/>
				</div>
				<div className="margin-top-small margin-bottom-small">
					<label className="bl-font-s1 text-gray-200">
						Duration: {duration}ms
					</label>
					<input
						value={duration}
						type="range"
						min={3000}
						max={15000}
						step={500}
						onChange={(e) => {
							setDuration(Number.parseInt(e.target.value, 10));
						}}
						style={{
							backgroundColor: colors.blGrayscale700,
							width: '100%',
							padding: '2px 0',
							marginTop: '8px',
						}}
					/>
				</div>
				<button
					onClick={() => triggerGeneralError()}
					className="button-pillbox primary small"
					style={{ marginTop: '8px' }}
				>
					Test Custom Error Alert
				</button>
			</div>

			<hr style={{ borderColor: colors.blGrayscale700, margin: '24px 0' }} />

			{/* Predefined Error Scenarios */}
			<div className="bl-font-s2 text-gray-100 margin-bottom-small">
				checkJob Error Scenarios
			</div>
			<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
				{Object.entries(ERROR_SCENARIOS).map(([key, scenario]) => {
					const isActive = activeScenario === scenario.value;
					return (
						<div
							key={key}
							style={{
								backgroundColor: isActive ? colors.blGrayscale700 : colors.blGrayscale800,
								borderRadius: '8px',
								padding: '12px',
								border: isActive ? '2px solid #4ade80' : 'none',
							}}
						>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									marginBottom: '8px',
								}}
							>
								<div className="bl-font-s1 text-dark">
									{isActive && '✓ '}
									{scenario.title}
								</div>
								<div style={{ display: 'flex', gap: '8px' }}>
									<button
										onClick={() => setDebugScenario(scenario.value)}
										disabled={isLoading}
										className={`button-pillbox ${isActive ? 'positive' : 'primary'} xsmall`}
									>
										{isActive ? 'Active' : 'Activate'}
									</button>
									<button
										onClick={() => triggerGeneralError(key as keyof typeof ERROR_SCENARIOS)}
										className="button-pillbox dullgray xsmall"
									>
										Preview Alert
									</button>
								</div>
							</div>
						<div className="bl-font-s1 text-gray-300" style={{ fontSize: '12px' }}>
							{scenario.description}
						</div>
						<details style={{ marginTop: '8px' }}>
							<summary
								className="bl-font-s1 text-gray-200"
								style={{ cursor: 'pointer', fontSize: '11px' }}
							>
								View mock response data
							</summary>
							<pre
								style={{
									backgroundColor: colors.blGrayscale900,
									padding: '8px',
									borderRadius: '4px',
									marginTop: '8px',
									fontSize: '10px',
									overflow: 'auto',
								}}
							>
								{JSON.stringify(scenario.mockData, null, 2)}
							</pre>
						</details>
						</div>
					);
				})}
			</div>

			<hr style={{ borderColor: colors.blGrayscale700, margin: '24px 0' }} />

			{/* Instructions */}
			<div className="margin-top-medium">
				<div className="bl-font-s2 text-gray-100 margin-bottom-small">
					How to Use
				</div>
				<div
					className="bl-font-s1 text-gray-200"
					style={{ fontSize: '12px', lineHeight: '1.6' }}
				>
					<p><strong>Testing Error Scenarios:</strong></p>
					<p>1. Click "Activate" on any scenario above</p>
					<p>2. Generate content in Greenroom (create or regenerate a title/graphic)</p>
					<p>3. The content-service will return the mocked error response</p>
					<p>4. Observe how the error is handled and displayed to the user</p>
					<p>5. Click "Disable Debug Mode" when finished testing</p>
					<br/>
					<p><strong>Preview Alerts:</strong></p>
					<p>• Use "Preview Alert" buttons to see how each error will appear without generating content</p>
					<p>• Use the custom alert section to test different messages and durations</p>
				</div>
			</div>
		</div>
	);
}
